import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { createFileRoute, Link } from '@tanstack/react-router'
import { EllipsisVerticalIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { updateAccountMutation, getAccountQuery } from '@/queries/accountMutation';
import type { AccountData } from '@/queries/accountMutation';
import { states } from '@/queries/states';



export const Route = createFileRoute('/_app/settings/org-settings/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { data: accountData, isLoading } = useQuery(getAccountQuery());
  const updateAccount = updateAccountMutation();

  const [formData, setFormData] = useState<AccountData>({
    id: '',
    name: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  });

  useEffect(() => {
    if (accountData) {
      setFormData(accountData);
    }
  }, [accountData]);

  const handleInputChange = (field: keyof AccountData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateAccount.mutateAsync(formData);
      // Show success message or handle success
    } catch (error) {
      // Handle error
      console.error('Failed to update account:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings/org-settings'>
                  Settings
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                Organization Info
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <Button
            onClick={handleSave}
            disabled={updateAccount.isPending}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {updateAccount.isPending ? 'Saving...' : 'Save'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="h-full w-full p-6 bg-gray-100 overflow-auto">
        <Card className="max-w-4xl">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Edit Organization Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Organization Name and Email */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="organizationName" className="text-sm font-medium">
                  Organization Name: <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="organizationName"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email:
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-base font-medium">Address Information</h3>

              <div>
                <Label htmlFor="address1" className="text-sm font-medium">
                  Address 1:
                </Label>
                <Input
                  id="address1"
                  value={formData.address1 || ''}
                  onChange={(e) => handleInputChange('address1', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="address2" className="text-sm font-medium">
                  Address 2:
                </Label>
                <Input
                  id="address2"
                  value={formData.address2 || ''}
                  onChange={(e) => handleInputChange('address2', e.target.value)}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city" className="text-sm font-medium">
                    City:
                  </Label>
                  <Input
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="state" className="text-sm font-medium">
                    State:
                  </Label>
                  <Select
                    value={formData.state || ''}
                    onValueChange={(value) => handleInputChange('state', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="zip" className="text-sm font-medium">
                    Zip:
                  </Label>
                  <Input
                    id="zip"
                    value={formData.zip || ''}
                    onChange={(e) => handleInputChange('zip', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
